// Stukjes van de weergave-opties uit het tabblad Weergave in het dashboard (golf 1A, Martijn 10-10-2026): het logo en de
// starttekst bovenaan, en het doorsturen na een geslaagde boeking. Zonder ingestelde optie tekenen ze niets.
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  VOORWAARDENVELD, doorsturenAdres, isVoorbeeld, logoAdres, voorwaardenAan, voorwaardenAdres, weergaveTekst,
} from "../lib/weergave";
import { naarOuder } from "../lib/bericht";
import { vertaal as jmVertaal } from "../lib/woorden";

/** Het logo van de zaak boven in de widget. */
export function WeergaveLogo({ weergave }) {
  const logo = logoAdres(weergave);
  if (!logo) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 1, sm: 1.5 } }}>
      <Box component="img" src={logo} alt="" sx={{ maxHeight: 64, maxWidth: "60%", objectFit: "contain" }} />
    </Box>
  );
}
WeergaveLogo.propTypes = { weergave: PropTypes.object };

/** De starttekst onder de kop. */
export function WeergaveStarttekst({ weergave, kleur }) {
  const tekst = weergaveTekst(weergave, "starttekst");
  if (!tekst) return null;
  return (
    <Typography sx={{ whiteSpace: "pre-line", mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "13px", sm: "14px" }, color: kleur }}>
      {tekst}
    </Typography>
  );
}
WeergaveStarttekst.propTypes = { weergave: PropTypes.object, kleur: PropTypes.string };

/** Een eigen bevestigingstekst op het successcherm. */
export function WeergaveBevestiging({ weergave, sx }) {
  const tekst = weergaveTekst(weergave, "bevestiging");
  if (!tekst) return null;
  return <Typography sx={{ whiteSpace: "pre-line", ...sx }}>{tekst}</Typography>;
}
WeergaveBevestiging.propTypes = { weergave: PropTypes.object, sx: PropTypes.object };

/**
 * Het vinkje "Ik ga akkoord met de voorwaarden" in het gastformulier, met de link naar de voorwaarden. Verplicht: de regel
 * staat in het formulier (metVoorwaarden in lib/weergave). Alleen als voorwaardenTonen aan staat.
 */
export function VoorwaardenVinkje({ weergave, values, errors, touched, setField, kleur, vinkkleur, groot }) {
  if (!voorwaardenAan(weergave) || typeof setField !== "function") return null;
  const adres = voorwaardenAdres(weergave);
  const fout = touched?.[VOORWAARDENVELD] && errors?.[VOORWAARDENVELD];
  return (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        sx={{ alignItems: "flex-start" }}
        control={
          <Checkbox
            size="small"
            checked={!!values?.[VOORWAARDENVELD]}
            onChange={(e) => setField(VOORWAARDENVELD, e.target.checked)}
            sx={{ pt: 0, "&.Mui-checked": { color: vinkkleur } }}
          />
        }
        label={
          <Typography sx={{ fontSize: groot ? "18px" : "14px", color: kleur }}>
            {weergaveTekst(weergave, "voorwaardentekst") || jmVertaal("Ik ga akkoord met de voorwaarden")}
            {adres && (
              <>
                {" ("}
                <Box component="a" href={adres} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()} sx={{ color: "inherit", textDecoration: "underline" }}>
                  {jmVertaal("lees de voorwaarden")}
                </Box>
                {")"}
              </>
            )}
          </Typography>
        }
      />
      {fout && (
        <Typography sx={{ color: "#d32f2f", fontSize: "12px", ml: 4 }}>{fout}</Typography>
      )}
    </Box>
  );
}
VoorwaardenVinkje.propTypes = {
  weergave: PropTypes.object, values: PropTypes.object, errors: PropTypes.object, touched: PropTypes.object,
  setField: PropTypes.func, kleur: PropTypes.string, vinkkleur: PropTypes.string, groot: PropTypes.bool,
};

/** Naar een adres gaan: in een iframe vraagt de pagina eromheen het (zoals bij betalen), anders zelf. */
function gaNaar(adres) {
  if (!adres || typeof window === "undefined") return;
  if (window.parent && window.parent !== window) naarOuder({ redirectUrl: adres });
  else window.location.href = adres;
}

/** Na een geslaagde boeking na vijf tellen doorsturen, met een link om meteen te gaan. Niet in het voorbeeld van het dashboard. */
export function WeergaveDoorsturen({ weergave, seconden = 5 }) {
  const adres = doorsturenAdres(weergave);
  const [over, setOver] = useState(seconden);
  const voorbeeld = typeof window !== "undefined" && isVoorbeeld(window.location.search);

  useEffect(() => {
    if (!adres || voorbeeld) return undefined;
    if (over <= 0) {
      gaNaar(adres);
      return undefined;
    }
    const klok = setTimeout(() => setOver((o) => o - 1), 1000);
    return () => clearTimeout(klok);
  }, [adres, over, voorbeeld]);

  if (!adres) return null;
  return (
    <Box sx={{ mt: 2, textAlign: "center", fontSize: "14px" }}>
      {!voorbeeld && over > 0 && <Box component="span" sx={{ mr: 1 }}>{jmVertaal("Je wordt over {0} seconden doorgestuurd.", over)}</Box>}
      <Box
        component="a"
        href={adres}
        onClick={(e) => { e.preventDefault(); if (!voorbeeld) gaNaar(adres); }}
        sx={{ fontWeight: 600, color: "inherit", textDecoration: "underline" }}
      >
        {jmVertaal("Ga verder")}
      </Box>
    </Box>
  );
}
WeergaveDoorsturen.propTypes = { weergave: PropTypes.object, seconden: PropTypes.number };
